import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { IResourcesProps } from "./Resources.interface";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import Accordion from "../../components/Accordion/Accordion";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import { useResources } from "../../shared/hooks/useResources";
import { CONTENT_BASE_URL, DEFAULT_LANGUAGE } from "../../shared/constants";
import { IRegion, IResource, IResourceItem } from "../../shared/interfaces/resources.interface";
import Select from "../../components/Forms/Select/Select";
import { useCountries } from "../../shared/hooks/useCountries";
import { ICountry } from "../../shared/interfaces/countries.interface";
import Link from "../../components/Link/Link";
import { routes } from "../../shared/router/routes";
import { getFormattedPath, markdownToHtml, markdownToPlainText } from "../../shared/helpers/utils";
import { useSearchParams } from "react-router-dom";

function Resources(props: IResourcesProps) {
    const [resources, setResources] = useState<IResource[]>();
    const [countries, setCountries] = useState<ICountry[]>();
    const [description, setDescription] = useState<string>();
    const [regions, setRegions] = useState<IRegion[]>([]);
    const [currentCountry, setCurrentCountry] = useState<string>();
    const [currentRegion, setCurrentRegion] = useState<string>();
    const [searchParams, setSearchParams] = useSearchParams();

    const { setIsLoading } = useContext(LoadingContext);
    const { translate, lang } = useContext(TranslationContext);
    const {
        isLoading,
        data,
        refetch: getResources
    } = useResources(lang ?? DEFAULT_LANGUAGE, currentCountry, {
        enabled: false
    });
    const { isLoading: isLoadingCountries, data: dataCountries } = useCountries(
        lang ?? DEFAULT_LANGUAGE
    );

    const hide_regions = useMemo(() => regions.length === 1, [regions]);

    useEffect(() => {
        if ((currentCountry && props.national) || !props.national) getResources();
        else setResources([]);
    }, [currentCountry]);

    useEffect(() => {
        setCurrentRegion(undefined);
        setResources([]);
    }, [currentCountry]);

    useEffect(() => {
        if (isLoading || !data?.success) return;
        setRegions(data.data?.regions ?? []);
        setDescription(
            markdownToPlainText(
                data.data?.description?.replace(/^\s*!\[[^\]]*\]\([^)]+\)\s*/, "") ?? ""
            )
        );
        if (props.national) setResources([])
        else setResources(data.data?.resources);
    }, [data]);

    useEffect(() => {
        if (isLoading || !data?.success) return;
        if (regions.length === 1) setCurrentRegion(regions[0].id);

        const region = searchParams.get("region");
        if (region) setCurrentRegion(region);
    }, [regions, searchParams]);

    useEffect(() => {
        if (isLoadingCountries || !dataCountries?.success) return;
        setCountries(dataCountries.data);

        const country = searchParams.get("country");
        if (country) setCurrentCountry(country)
    }, [dataCountries, searchParams]);

    useEffect(() => {
        const region = regions?.find(c => c.id === currentRegion);
        if (!region) return;

        setResources(region?.resources ?? []);
    }, [currentRegion]);

    useEffect(() => {
        setIsLoading(isLoading || isLoadingCountries);
    }, [isLoading, isLoadingCountries]);

    useEffect(() => {
        if (!currentCountry || !currentRegion) return;
        setSearchParams({
            country: currentCountry,
            region: currentRegion
        });
    }, [currentCountry, currentRegion]);

    const getBody = useCallback((item: IResource) => {
        const getItem = (item: IResourceItem, i: number) => {
            return (
                <li className="py-2" key={i}>
                    {!!item.logo && <img src={`${CONTENT_BASE_URL}${item.logo.url}`} alt={item.link.text} style={{ maxWidth: 150 }} className="w-100 mb-1" />}
                    <a
                        rel="noreferrer"
                        target="_blank"
                        href={item.link.href}
                        className="d-flex justify-content-between align-items-center text-h5-bd color-neutrals-black"
                    >
                        {item.link.text}
                        <i className={`icon icon-24 icon-${item.type === "LINK" ? "ungroup" : "Phone"}`}></i>
                    </a>
                    {item.description && <div className="mb-0 rich_text" dangerouslySetInnerHTML={{ __html: markdownToHtml(item.description) }}></div>}
                </li>
            );
        };

        return (
            <>
                {item.subsections.map((subsection, i) => {
                    if (subsection.subtitle) {
                        return (
                            <div key={i} className="subsection">
                                <p className="text-bd2-rg color-neutrals-black mt-2 mb-n1">{subsection.subtitle}</p>
                                <ul>{subsection.resources.map((item, i) => getItem(item, i))}</ul>
                            </div>
                        );
                    } else {
                        return <ul key={i} className="subsection">{subsection.resources.map((item, i) => getItem(item, i))}</ul>
                    }
                })}
            </>
        );
    }, []);

    return (
        <div className="container-fluid gx-2 pt-2 pb-5">
            <div className="resources">
                <h1 className="text-h3-md mb-1">
                    {props.national
                        ? translate(
                            "common.national_resources_services",
                            "National Resources and Services"
                        )
                        : translate(
                            "common.international_resources_services",
                            "International Resources and Services"
                        )}
                </h1>
                {!!description && props.national &&
                    <div className="mb-12">
                        <div
                            className="text-bd1-rg color-neutrals-dark m-0"
                            dangerouslySetInnerHTML={{
                                __html: description.length > 150 ?
                                    markdownToHtml(description.slice(0, 150) + "...") :
                                    markdownToHtml(description.slice(0, 150))
                            }}>
                        </div>
                        {description.length > 150 && <Link
                            to={getFormattedPath(routes.RESOURCES_CONTENT.path, { country: currentCountry })}
                            customCss="w-100 color-blue-500 text-bd2-bd mt-02">
                            {translate("common.read_more", "Read more")}
                        </Link>}
                    </div>}
                {props.national && (
                    <div className="mb-12 d-flex flex-column gap-12">
                        <Select
                            filled
                            openByDefault={!currentCountry}
                            placeholder="Country"
                            value={currentCountry}
                            options={
                                countries?.map((c) => ({ value: c.isoCode, label: c.name })) ?? []
                            }
                            onChange={(e) => setCurrentCountry(e.value)}
                        />
                        {(regions.length > 0 && !hide_regions) && <Select
                            filled
                            placeholder="Region"
                            value={currentRegion}
                            openByDefault={!currentRegion}
                            options={
                                regions?.map((c) => ({ value: c.id, label: c.region })) ?? []
                            }
                            onChange={(e) => setCurrentRegion(e.value)}
                        />}
                    </div>
                )}
                <div className={`resources_content d-flex flex-column gap-22 color-neutrals-black ${props.national ? "" : "pt-02"}`}>
                    <Accordion
                        items={
                            resources?.map((r) => ({
                                title: r.title,
                                body: getBody(r)
                            })) ?? []
                        }
                    />
                </div>
            </div>
        </div>
    );
}

export default Resources;
